// pages/ProjectsPage.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import ProjectProfitability from "./ProjectProfitability";
import { db, auth } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import DeleteIcon from "@mui/icons-material/Delete";

import ProjectDetails from "../components/projects/ProjectDetails"; // Import ProjectDetails
import { formatCurrency } from "../utils/currency";

export default function ProjectsPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [projects, setProjects] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const [name, setName] = useState("");
  const [editingProject, setEditingProject] = useState(null);
  const [deleteProject, setDeleteProject] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchProjects = async () => {
    try {
      const user = auth.currentUser;
      console.log("Current user:", user);
      if (!user) throw new Error("User not logged in");

      const snap = await getDocs(
        query(collection(db, "projects"), where("uid", "==", user.uid)),
      );

      const projectList = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log("Fetched projects:", projectList);

      // Compute net profit/loss for each project
      const projectsWithNet = await Promise.all(
        projectList.map(async (project) => {
          console.log("Fetching net for project", project.id, project.name);
          const net = await fetchNetForProject(project.id);
          console.log(`Net for project ${project.name}:`, net);
          return { ...project, net };
        }),
      );

      setProjects(projectsWithNet);
    } catch (error) {
      console.error("Error fetching projects:", error);
      showSnackbar("Failed to fetch projects.", "error");
    }
  };

  const handleAddProject = async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not logged in");
      await addDoc(collection(db, "projects"), {
        uid: user.uid,
        name,
        createdAt: Timestamp.now(),
      });
      setOpenAdd(false);
      setName("");
      showSnackbar("Project created successfully!");
      fetchProjects();
    } catch (error) {
      console.error("Error adding project:", error);
      showSnackbar("Failed to add project.", "error");
    }
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setName(project.name);
    setOpenEdit(true);
  };

  const handleEditProject = async () => {
    if (!editingProject) return;
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not logged in");
      const projectRef = doc(db, "projects", editingProject.id);
      await updateDoc(projectRef, { name });
      setOpenEdit(false);
      setEditingProject(null);
      setName("");
      showSnackbar("Project updated successfully!");
      fetchProjects();
    } catch (error) {
      console.error("Error updating project:", error);
      showSnackbar("Failed to update project.", "error");
    }
  };

  const openDeleteModal = (project) => {
    setDeleteProject(project);
    setOpenDelete(true);
  };

  const handleDeleteProject = async () => {
    if (!deleteProject) return;
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not logged in");
      const projectRef = doc(db, "projects", deleteProject.id);
      await deleteDoc(projectRef);
      setOpenDelete(false);
      setDeleteProject(null);
      showSnackbar("Project deleted successfully!");
      fetchProjects();
    } catch (error) {
      console.error("Error deleting project:", error);
      showSnackbar("Failed to delete project.", "error");
    }
  };

  const handleViewDetails = (projectId) => {
    setSelectedProjectId(projectId);
    setDetailsOpen(true);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const highlightMatch = (text, term) => {
    if (!term) return text;

    const lowerText = text.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lowerText.indexOf(lowerTerm);

    if (index === -1) return text;

    return (
      <>
        {text.substring(0, index)}
        <Box
          component="span"
          sx={{ backgroundColor: "yellow", borderRadius: "2px", px: 0.5 }}
        >
          {text.substring(index, index + term.length)}
        </Box>
        {text.substring(index + term.length)}
      </>
    );
  };

  const fetchNetForProject = async (projectId) => {
    let totalInvoices = 0;
    let totalReceipts = 0;
    let totalPayroll = 0;

    try {
      const user = auth.currentUser;
      if (!user) return 0;

      const invoicesSnap = await getDocs(
        query(
          collection(db, "invoices"),
          where("projectId", "==", projectId),
          where("userId", "==", user.uid),
        ),
      );
      totalInvoices = invoicesSnap.docs.reduce((sum, doc) => {
        const d = doc.data();
        if (d.status === "paid") {
          return sum + (d.totalAmount || 0);
        }
        return sum;
      }, 0);
      console.log(`Total paid invoices for ${projectId}:`, totalInvoices);

      const receiptsSnap = await getDocs(
        query(
          collection(db, "receipts"),
          where("projectId", "==", projectId),
          where("uid", "==", user.uid),
        ),
      );
      totalReceipts = receiptsSnap.docs.reduce(
        (sum, doc) => sum + (doc.data().amount || 0),
        0,
      );
      console.log(`Total receipts for ${projectId}:`, totalReceipts);

      const payrollSnap = await getDocs(
        query(
          collection(db, "payroll"),
          where("projectId", "==", projectId),
          where("uid", "==", user.uid),
        ),
      );
      totalPayroll = payrollSnap.docs.reduce(
        (sum, doc) => sum + (doc.data().amount || 0),
        0,
      );
      console.log(`Total payroll for ${projectId}:`, totalPayroll);

      console.log(
        "Invoice docs:",
        invoicesSnap.docs.map((d) => d.data()),
      );
      console.log(
        "Receipt docs:",
        receiptsSnap.docs.map((d) => d.data()),
      );
      console.log(
        "Payroll docs:",
        payrollSnap.docs.map((d) => d.data()),
      );

      return totalInvoices - (totalReceipts + totalPayroll);
    } catch (err) {
      console.error(`Error calculating net for project ${projectId}:`, err);
      return 0;
    }
  };
  return (
    <Box>
      <Typography
        variant="h5"
        fontWeight={800}
        letterSpacing={-0.5}
        gutterBottom
      >
        Projects
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            minHeight: 42,
          },
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: 2,
          },
        }}
      >
        <Tab label="My Projects" />
        <Tab label="Profitability" />
      </Tabs>

      {activeTab === 1 && <ProjectProfitability />}

      {activeTab === 0 && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Organize invoices and receipts per project. See net balance at a
            glance.
          </Typography>
          <Button variant="contained" onClick={() => setOpenAdd(true)}>
            + New Project
          </Button>

          <TextField
            label="Search Projects"
            variant="outlined"
            fullWidth
            sx={{ mt: 3, mb: 2 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Stack spacing={2} mt={3}>
            {projects
              .filter((project) =>
                project.name.toLowerCase().includes(searchTerm.toLowerCase()),
              )
              .map((project) => (
                <Paper
                  key={project.id}
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {highlightMatch(project.name, searchTerm)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      label={`${project.net >= 0 ? "+" : ""}${formatCurrency(
                        project.net,
                      )}`}
                      color={project.net >= 0 ? "success" : "error"}
                      variant="outlined"
                    />

                    <Button
                      onClick={() => handleViewDetails(project.id)}
                      size="small"
                    >
                      View Details
                    </Button>
                    <IconButton
                      color="primary"
                      onClick={() => openEditModal(project)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => openDeleteModal(project)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
          </Stack>
        </>
      )}

      {/* Add Project Dialog */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)}>
        <DialogTitle>Add Project</DialogTitle>
        <DialogContent>
          <TextField
            label="Project Name"
            fullWidth
            sx={{ mt: 2 }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button
            onClick={handleAddProject}
            variant="contained"
            disabled={!name.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <TextField
            label="Project Name"
            fullWidth
            sx={{ mt: 2 }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button
            onClick={handleEditProject}
            variant="contained"
            disabled={!name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the project "{deleteProject?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteProject}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Details Dialog */}
      <ProjectDetails
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        projectId={selectedProjectId}
      />

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
